import React, { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import { QrCode, ExternalLink, Download, Copy, CheckCircle, Loader2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MyQRAccess - Reusable component for QR code retrieval/access
 * 
 * Shows different states:
 * 1. Wallet not connected → Prompt to connect
 * 2. Wallet connected, no QR → CTA to create QR
 * 3. Wallet connected, QR exists → Show QR with actions
 * 
 * Props:
 * - variant: 'compact' | 'full' (default: 'compact')
 * - showWhenNoQR: boolean (default: true) - show create CTA when no QR exists
 * - className: additional CSS classes
 */
export const MyQRAccess = ({ 
  variant = 'compact', 
  showWhenNoQR = true,
  className = '' 
}) => {
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  // Fetch QR data when wallet connects
  useEffect(() => {
    if (isConnected && address) {
      fetchQRData();
    } else {
      setQrData(null);
      setShowQR(false);
    }
  }, [isConnected, address]);

  const fetchQRData = async () => {
    if (!address) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/qr/wallet/${address}`);
      setQrData(response.data);
    } catch (err) {
      console.error('[MyQRAccess] Failed to fetch QR:', err);
      setError('Failed to load QR data');
    } finally {
      setLoading(false);
    }
  };

  const qrUrl = qrData?.qrId 
    ? `${window.location.origin}/tip/${qrData.qrId}`
    : null;

  const copyToClipboard = async () => {
    if (!qrUrl) return;
    try {
      await navigator.clipboard.writeText(qrUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const downloadQR = () => {
    if (!qrData?.qrId) return;
    
    const svg = document.getElementById(`qr-svg-${qrData.qrId}`);
    if (!svg) return;
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    canvas.width = 512;
    canvas.height = 512;
    
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 0, 0, 512, 512);
      
      const link = document.createElement('a');
      link.download = `tipme-qr-${qrData.qrId}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // STATE 1: Wallet not connected
  if (!isConnected) {
    if (variant === 'compact') {
      return null; // Don't show anything in compact mode when not connected
    }
    return (
      <div className={`bg-[#e8e3db] rounded-xl p-4 ${className}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#f0b90b]/20 rounded-full flex items-center justify-center">
            <QrCode className="w-5 h-5 text-[#f0b90b]" />
          </div>
          <div>
            <p className="text-sm font-medium text-[#1a1a1a]">My QR Code</p>
            <p className="text-xs text-[#666]">Connect wallet to access your QR</p>
          </div>
        </div>
      </div>
    );
  }

  // STATE 2: Loading
  if (loading) {
    return (
      <div className={`bg-[#e8e3db] rounded-xl p-4 ${className}`}>
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-[#f0b90b] animate-spin" />
          <span className="text-sm text-[#666]">Loading your QR...</span>
        </div>
      </div>
    );
  }

  // STATE 3: No QR exists
  if (!qrData?.exists) {
    if (!showWhenNoQR) return null;
    
    return (
      <div className={`bg-[#e8e3db] rounded-xl p-4 ${className}`} data-testid="my-qr-create-cta">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#f0b90b]/20 rounded-full flex items-center justify-center">
              <QrCode className="w-5 h-5 text-[#f0b90b]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#1a1a1a]">Get Your QR Code</p>
              <p className="text-xs text-[#666]">Receive tips in person with a unique QR</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/register-qr')}
            className="bg-[#f0b90b] hover:bg-[#d4a50a] text-white text-sm px-4 py-2 h-auto"
            data-testid="create-qr-btn"
          >
            Create QR
          </Button>
        </div>
      </div>
    );
  }

  // STATE 4: QR exists - Compact view (collapsed)
  if (variant === 'compact' && !showQR) {
    return (
      <div className={`bg-[#e8e3db] rounded-xl p-4 ${className}`} data-testid="my-qr-access">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#f0b90b] rounded-full flex items-center justify-center">
              <QrCode className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#1a1a1a]">My QR Code</p>
              <p className="text-xs text-[#666]">Your personal tip link is ready</p>
            </div>
          </div>
          <Button
            onClick={() => setShowQR(true)}
            className="bg-[#1a1a1a] hover:bg-[#333] text-white text-sm px-4 py-2 h-auto"
            data-testid="view-qr-btn"
          >
            View QR
          </Button>
        </div>
      </div>
    );
  }

  // STATE 5: QR exists - Expanded view (showing QR)
  return (
    <div className={`bg-[#e8e3db] rounded-xl p-5 ${className}`} data-testid="my-qr-display">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#f0b90b] rounded-full flex items-center justify-center">
            <QrCode className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-medium text-[#1a1a1a]">My QR Code</p>
            <p className="text-xs text-[#666]">Always available here</p>
          </div>
        </div>
        {variant === 'compact' && (
          <button 
            onClick={() => setShowQR(false)}
            className="text-xs text-[#666] hover:text-[#1a1a1a]"
          >
            Collapse
          </button>
        )}
      </div>
      
      {/* QR Code */}
      <div className="flex justify-center mb-4">
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <QRCodeSVG
            id={`qr-svg-${qrData.qrId}`}
            value={qrUrl}
            size={160}
            level="H"
            includeMargin={true}
          />
        </div>
      </div>
      
      {/* Label if exists */}
      {qrData.label && (
        <p className="text-center text-sm text-[#666] mb-3">
          {qrData.label}
        </p>
      )}
      
      {/* Actions */}
      <div className="flex gap-2 justify-center">
        <Button
          onClick={copyToClipboard}
          variant="outline"
          className="flex items-center gap-2 text-sm h-9 px-3 border-[#ccc8bc]"
          data-testid="copy-qr-link"
        >
          {copied ? (
            <>
              <CheckCircle className="w-4 h-4 text-green-600" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy Link
            </>
          )}
        </Button>
        
        <Button
          onClick={downloadQR}
          variant="outline"
          className="flex items-center gap-2 text-sm h-9 px-3 border-[#ccc8bc]"
          data-testid="download-qr"
        >
          <Download className="w-4 h-4" />
          Download
        </Button>
        
        <Button
          onClick={() => window.open(qrUrl, '_blank')}
          variant="outline"
          className="flex items-center gap-2 text-sm h-9 px-3 border-[#ccc8bc]"
          data-testid="open-qr-link"
        >
          <ExternalLink className="w-4 h-4" />
          Open
        </Button>
      </div>
    </div>
  );
};

export default MyQRAccess;
